import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import axios from "@/lib/axios";
import { useToast } from "@/components/ui/use-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Search, Play, VolumeX, Save, Edit, X, Music, Mic, RefreshCw, Trash2 } from "lucide-react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

interface VoiceSettings {
  similarity?: number;
  stability?: number;
  speakerBoost?: boolean;
  speed?: number;
  style?: number;
}

interface Voice {
  voice_id: string;
  voiceId?: string;          // API yanıtlarında bazen bu formatta gelebiliyor
  name: string;
  category: string;
  preview_url?: string;       // ElevenLabs API'den gelen orijinal alan
  previewAudioUrl: string;    // Veritabanımızda kullanılan alan
  model_id: string;
  language?: string;
  languages?: string[];      // Birden fazla dil olduğunda kullanılır
  labels?: Record<string, string>;
  enabled: boolean;          // Eski kullanım
  isActive: boolean;         // Veritabanında kullanılan aktif durumu
  gender?: string;           // Cinsiyet alanı
  tags?: string[];           // Ses modellerine eklenebilen etiketler
  settings?: VoiceSettings;   // Ses ayarları (similarity, stability, style, speakerBoost)
}

interface VoiceModelSettings {
  enabledVoiceIds: string[];
  defaultLanguage: string;
  availableLanguages: string[];
}

const VoiceModelSettings = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  // State yok, global değişken yok
  // Her modelin kendi ses oynatıcısını oluşturup yöneteceğiz - DOM manipulasyonu
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [selectedLanguage, setSelectedLanguage] = useState<string>("all");
  const [selectedGender, setSelectedGender] = useState<string>("all");
  const [editingVoice, setEditingVoice] = useState<Voice | null>(null);
  const [deletingVoice, setDeletingVoice] = useState<Voice | null>(null);
  const [voiceTags, setVoiceTags] = useState<string[]>([]);
  const [voiceLanguages, setVoiceLanguages] = useState<string[]>([]);
  const [newTagInput, setNewTagInput] = useState<string>("");

  // Desteklenen diller listesi
  const AVAILABLE_LANGUAGES = [
    { code: 'tr', name: 'Türkçe' },
    { code: 'en', name: 'İngilizce' },
    { code: 'de', name: 'Almanca' },
    { code: 'fr', name: 'Fransızca' },
    { code: 'es', name: 'İspanyolca' },
    { code: 'it', name: 'İtalyanca' },
    { code: 'pt', name: 'Portekizce' },
    { code: 'ru', name: 'Rusça' },
    { code: 'ar', name: 'Arapça' },
    { code: 'zh', name: 'Çince' },
    { code: 'ja', name: 'Japonca' },
    { code: 'ko', name: 'Korece' },
    { code: 'hi', name: 'Hintçe' },
    { code: 'pl', name: 'Lehçe' },
    { code: 'nl', name: 'Hollandaca' },
    { code: 'sv', name: 'İsveççe' },
    { code: 'el', name: 'Yunanca' },
    { code: 'ro', name: 'Romence' },
    { code: 'hu', name: 'Macarca' },
    { code: 'cs', name: 'Çekçe' },
    { code: 'uk', name: 'Ukraynaca' },
    { code: 'hr', name: 'Hırvatça' },
    { code: 'sk', name: 'Slovakça' },
    { code: 'da', name: 'Danca' },
    { code: 'fi', name: 'Fince' },
    { code: 'no', name: 'Norveççe' },
  ];

  // Ses modellerini çekme
  const { data: voices, isLoading } = useQuery({
    queryKey: ["voice-models"],
    queryFn: async () => {
      const { data } = await axios.get("/ai-announcements/voices");
      return data.map((voice: any) => ({
        ...voice,
        preview_url: voice.previewAudioUrl,
        enabled: true,
      }));
    },
  });

  // Ses modeli ayarlarını çekme
  const { data: settings } = useQuery({
    queryKey: ["voice-model-settings"],
    queryFn: async () => {
      const { data } = await axios.get("/ai-announcements/settings");
      return data;
    },
  });
  
  // Ses modellerini ElevenLabs'dan senkronize etme mutasyonu
  const syncVoiceModels = useMutation({
    mutationFn: async () => {
      const { data } = await axios.post("/ai-announcements/voices/sync");
      return data;
    },
    onSuccess: (data) => {
      toast({
        title: "Ses Modelleri Senkronize Edildi",
        description: `${data.result.upserted} yeni model eklendi, ${data.result.modified} model güncellendi.`,
        duration: 5000,
      });
      // Ses modellerini yeniden yükle
      queryClient.invalidateQueries({ queryKey: ["voice-models"] });
    },
    onError: (error) => {
      toast({
        title: "Senkronizasyon Hatası",
        description: `Ses modelleri senkronize edilemedi: ${error instanceof Error ? error.message : 'Bilinmeyen hata'}`,
        variant: "destructive",
        duration: 5000,
      });
    },
  });

  // Ses modeli güncelleme mutasyonu
  const updateVoiceModel = useMutation({
    mutationFn: async (voice: Partial<Voice> & { voice_id: string; isActive?: boolean; languages?: string[] }) => {
      if (!voice.voice_id) {
        throw new Error('Ses modeli ID si bulunamadı');
      }

      const updateData: Record<string, any> = {
        isActive: voice.isActive,
        tags: voice.tags,
        languages: voice.languages,
        gender: voice.gender
      };

      if (voice.name) {
        updateData.name = voice.name;
      }

      if (voice.settings?.stability !== undefined) {
        updateData.stability = voice.settings.stability;
      }

      const { data } = await axios.put(`/ai-announcements/voices/${voice.voice_id}/settings`, updateData);
      return data;
    },
    onSuccess: () => {
      toast({
        title: "Ses Modeli Güncellendi",
        description: "Ses modeli ayarları başarıyla kaydedildi.",
      });
      queryClient.invalidateQueries({ queryKey: ["voice-models"] });
      setEditingVoice(null);
    },
    onError: () => {
      toast({
        title: "Hata",
        description: "Ses modeli güncellenirken bir hata oluştu.",
        variant: "destructive",
      });
    },
  });

  // Toplu güncelleme mutasyonu
  const updateVoiceSettings = useMutation({
    mutationFn: async (settings: Partial<VoiceModelSettings>) => {
      const { data } = await axios.put("/ai-announcements/settings/language", {
        defaultLanguage: settings.defaultLanguage,
        supportedLanguages: settings.availableLanguages
      });
      return data;
    },
    onSuccess: () => {
      toast({
        title: "Ayarlar Güncellendi",
        description: "Dil ayarları başarıyla kaydedildi.",
      });
      queryClient.invalidateQueries({ queryKey: ["voice-model-settings"] });
      queryClient.invalidateQueries({ queryKey: ["voice-models"] });
    },
    onError: () => {
      toast({
        title: "Hata",
        description: "Dil ayarları güncellenirken bir hata oluştu.",
        variant: "destructive",
      });
    },
  });

  // ÖZEL AUDIO DÜĞME KONTROLÜ - İki tıklamayla ilgili sorunu tamamen çözmek için özel bir audio yöneticisi
  // Bütün ses işlemleri için global bir ses yöneticisi
  const PLAY_ICON = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="h-4 w-4 mr-1"><polygon points="5 3 19 12 5 21 5 3"></polygon></svg>Dinle';
  const PAUSE_ICON = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="h-4 w-4 mr-1"><rect x="6" y="4" width="4" height="16"></rect><rect x="14" y="4" width="4" height="16"></rect></svg>Durdur';
  
  let currentAudio: HTMLAudioElement | null = null;
  let currentButton: HTMLButtonElement | null = null;
  
  // Tüm ses oynatıcıları durdur
  const stopAllAudio = () => {
    if (currentAudio) {
      currentAudio.pause();
      if (currentButton) {
        currentButton.innerHTML = PLAY_ICON;
        currentButton.setAttribute('data-playing', 'false');
      }
      currentAudio = null;
      currentButton = null;
    }
  };
  
  // Bu butona her tıklandığında çağrılacak basit, senkron fonksiyon
  function toggleAudio(voiceId: string, audioUrl: string, button: HTMLButtonElement) {
    console.log('Toggle audio', voiceId, button.getAttribute('data-playing'));
    
    const isPlaying = button.getAttribute('data-playing') === 'true';
    
    // Eğer bu düğme çalıyorsa, durdur
    if (isPlaying) {
      console.log('Stopping audio');
      if (currentAudio) {
        currentAudio.pause();
      }
      button.innerHTML = PLAY_ICON;
      button.setAttribute('data-playing', 'false');
      return;
    }
    
    // Önce çalan varsa durdur
    stopAllAudio();
    
    // Sonra yeni sesi çal
    try {
      // Önce düğmeyi güncelle - anlık geri bildirim
      button.innerHTML = PAUSE_ICON;
      button.setAttribute('data-playing', 'true');
      
      // Yeni bir audio nesnesi oluştur
      const audio = new Audio(audioUrl);
      
      // Ses bittiğinde çağrılacak işlev
      audio.addEventListener('ended', function() {
        button.innerHTML = PLAY_ICON;
        button.setAttribute('data-playing', 'false');
        currentAudio = null;
        currentButton = null;
      });
      
      // Durumu güncelle
      currentAudio = audio;
      currentButton = button;
      
      // Sesi çal (error handling için try-catch içinde)
      audio.play().catch(error => {
        console.error('Ses çalma hatası:', error);
        button.innerHTML = PLAY_ICON;
        button.setAttribute('data-playing', 'false');
        toast({
          title: "Ses Oynatma Hatası",
          description: "Ses örneği oynatılamadı. Lütfen tekrar deneyin.",
          variant: "destructive",
        });
      });
    } catch (error) {
      console.error('Ses çalma hatası:', error);
      button.innerHTML = PLAY_ICON;
      button.setAttribute('data-playing', 'false');
      toast({
        title: "Ses Oynatma Hatası",
        description: "Ses örneği oynatılamadı. Lütfen tekrar deneyin.",
        variant: "destructive",
      });
    }
  }
  
  // TEK TIKLAMADA ÇALIŞAN basit fonksiyon
  const toggleVoicePreview = (voiceId: string, audioUrl: string, buttonElement: HTMLButtonElement) => {
    // Doğrudan ve anında tepki veren fonksiyonu çağır
    toggleAudio(voiceId, audioUrl, buttonElement);
  };

  // Bu fonksiyon artık kullanılmıyor, onended olayı içinde doğrudan işleniyor
  // Audio API'ye geçildiği için audio elementlerine ihtiyaç kalmadı

  // Ses modeli durumu güncelleme mutasyonu
  const updateVoiceStatus = useMutation({
    mutationFn: async ({ voiceId, isActive }: { voiceId: string; isActive: boolean }) => {
      const { data } = await axios.put(`/ai-announcements/voices/${voiceId}/status`, { isActive });
      return data;
    },
    onSuccess: () => {
      toast({
        title: "Durum Güncellendi",
        description: "Ses modeli durumu başarıyla güncellendi.",
      });
      queryClient.invalidateQueries({ queryKey: ["voice-models"] });
    },
    onError: () => {
      toast({
        title: "Hata",
        description: "Ses modeli durumu güncellenirken bir hata oluştu.",
        variant: "destructive",
      });
    },
  });

  // Ses modeli silme mutasyonu (ElevenLabs + DB)
  const deleteVoiceModel = useMutation({
    mutationFn: async (voiceId: string) => {
      const { data } = await axios.delete(`/ai-announcements/voice-models/${voiceId}`);
      return data;
    },
    onSuccess: () => {
      toast({
        title: "Ses Modeli Silindi",
        description: "Ses modeli ElevenLabs ve sistemden kalıcı olarak silindi.",
      });
      setDeletingVoice(null);
      queryClient.invalidateQueries({ queryKey: ["voice-models"] });
    },
    onError: (error) => {
      toast({
        title: "Silme Hatası",
        description: `Ses modeli silinemedi: ${error instanceof Error ? error.message : 'Bilinmeyen hata'}`,
        variant: "destructive",
      });
    },
  });

  // Ses modelini etkinleştir/devre dışı bırak
  const toggleVoiceEnabled = (voiceId: string, isActive: boolean) => {
    updateVoiceStatus.mutate({ voiceId, isActive });
  };

  // Dil filtresi değiştiğinde seçili dili güncelle
  const handleLanguageChange = (language: string) => {
    setSelectedLanguage(language);
  };

  // Ses modelini düzenleme için ayarla
  const handleEditVoice = (voice: Voice) => {
    setEditingVoice(voice);
    setVoiceTags(voice.tags || []);
    setVoiceLanguages(voice.languages || []);
  };

  // Düzenleme işlemini kaydet
  const handleSaveVoiceEdit = () => {
    if (editingVoice) {
      const modelId = editingVoice.voiceId || editingVoice.voice_id;

      if (!modelId) {
        toast({
          title: "Hata",
          description: "Ses modeli ID'si eksik. Düzenleme yapılamadı.",
          variant: "destructive",
        });
        return;
      }

      const updatedVoice = {
        voice_id: modelId,
        name: editingVoice.name,
        isActive: editingVoice.isActive,
        gender: editingVoice.gender,
        tags: voiceTags,
        languages: voiceLanguages,
        settings: editingVoice.settings ? {
          similarity: editingVoice.settings.similarity,
          stability: editingVoice.settings.stability,
          style: editingVoice.settings.style,
          speakerBoost: editingVoice.settings.speakerBoost
        } : undefined
      };

      updateVoiceModel.mutate(updatedVoice);
    }
  };

  // Etiket ekleme
  const handleAddTag = () => {
    if (newTagInput.trim() && !voiceTags.includes(newTagInput.trim())) {
      setVoiceTags([...voiceTags, newTagInput.trim()]);
      setNewTagInput("");
    }
  };

  // Etiket silme
  const handleRemoveTag = (tag: string) => {
    setVoiceTags(voiceTags.filter(t => t !== tag));
  };

  // Dil seçenekleri
  const languageOptions = [
    { value: "all", label: "Tüm Diller" },
    { value: "tr", label: "Türkçe" },
    { value: "en", label: "İngilizce" },
  ];
  
  // Mevcut dilleri listeye ekle
  if (settings?.availableLanguages) {
    settings.availableLanguages.forEach((lang: string) => {
      if (!languageOptions.some(option => option.value === lang) && lang !== "all") {
        const langName = lang === "tr" ? "Türkçe" : 
                       lang === "en" ? "İngilizce" : 
                       lang.toUpperCase();
        languageOptions.push({ value: lang, label: langName });
      }
    });
  }

  // Ses modellerini işle ve etiketleri ekstra olarak ekle
  const processedVoices = voices?.map((voice: Voice) => {
    // Eğer etiketler boşsa veya yoksa ve labels varsa
    if ((!voice.tags || voice.tags.length === 0) && voice.labels) {
      const tags: string[] = [];
      // Labels'dan etiketleri ekle
      Object.values(voice.labels).forEach(value => {
        if (value && typeof value === 'string') {
          tags.push(value.toLowerCase());
        }
      });
      // Standart etiketleri ekle
      if (!tags.includes('professional')) {
        tags.push('professional');
      }
      return { ...voice, tags };
    }
    return voice;
  });

  // Filtrelenmiş sesleri hazırla
  const filteredVoices = processedVoices?.filter((voice: Voice) => {
    // Arama sorgusu filtresi
    const matchesSearch = searchQuery === "" ||
      voice.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (voice.tags && voice.tags.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase())));

    // Dil filtresi (backend voice.languages array döner)
    const matchesLanguage =
      selectedLanguage === "all" ||
      (voice.languages && voice.languages.includes(selectedLanguage));

    // Cinsiyet filtresi
    const matchesGender =
      selectedGender === "all" ||
      voice.gender === selectedGender;

    return matchesSearch && matchesLanguage && matchesGender;
  });

  return (
    <div className="container mx-auto py-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">AI Ses Modelleri</h2>
          <p className="text-muted-foreground">AI Anons sistemi için kullanılabilir ses modellerini yönetin</p>
        </div>
        <div className="flex gap-2">
          <Button 
            variant="default" 
            onClick={() => syncVoiceModels.mutate()}
            disabled={syncVoiceModels.isPending}
          >
            {syncVoiceModels.isPending ? (
              <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <RefreshCw className="w-4 h-4 mr-2" />
            )}
            ElevenLabs'dan Senkronize Et
          </Button>
          <Button 
            variant="outline" 
            onClick={() => queryClient.invalidateQueries({ queryKey: ['voice-models'] })}
            disabled={isLoading}
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Yenile
          </Button>
        </div>
      </div>

      <Tabs defaultValue="voice-models">
        <TabsList className="mb-4">
          <TabsTrigger value="voice-models">
            <Mic className="w-4 h-4 mr-2" />
            Ses Modelleri
          </TabsTrigger>
          <TabsTrigger value="language-settings">
            <Music className="w-4 h-4 mr-2" />
            Dil Ayarları
          </TabsTrigger>
        </TabsList>

        <TabsContent value="voice-models">
          <div className="mb-6 flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                type="search"
                placeholder="Ses modeli ara..."
                className="pl-8"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
            <Select value={selectedLanguage} onValueChange={handleLanguageChange}>
              <SelectTrigger className="w-full sm:w-[180px]">
                <SelectValue placeholder="Dil Seçin" />
              </SelectTrigger>
              <SelectContent>
                {languageOptions.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={selectedGender} onValueChange={setSelectedGender}>
              <SelectTrigger className="w-full sm:w-[180px]">
                <SelectValue placeholder="Cinsiyet Seçin" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tüm Cinsiyetler</SelectItem>
                <SelectItem value="female">Kadın</SelectItem>
                <SelectItem value="male">Erkek</SelectItem>
                <SelectItem value="other">Diğer</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <Card>
            <CardHeader>
              <CardTitle>Ses Modelleri</CardTitle>
              <CardDescription>
                ElevenLabs'ten alınan ve sistemde kullanılabilir olan ses modelleri
              </CardDescription>
            </CardHeader>
            <CardContent>
              {isLoading ? (
                <div className="space-y-4">
                  <Skeleton className="h-12 w-full" />
                  <Skeleton className="h-12 w-full" />
                  <Skeleton className="h-12 w-full" />
                </div>
              ) : (
                <div className="border rounded-md">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Ses Modeli</TableHead>
                        <TableHead>Dil</TableHead>
                        <TableHead>Cinsiyet</TableHead>
                        <TableHead>Etiketler</TableHead>
                        <TableHead>Ön İzleme</TableHead>
                        <TableHead>Durumu</TableHead>
                        <TableHead className="text-right">İşlemler</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredVoices && filteredVoices.length > 0 ? (
                        filteredVoices.map((voice: Voice) => (
                          <TableRow key={voice.voice_id}>
                            <TableCell className="font-medium">
                              {voice.name}
                              <div className="text-xs text-muted-foreground">
                                Model: {voice.model_id}
                              </div>
                            </TableCell>
                            <TableCell>
                              {voice.language || 
                                (voice.labels && voice.labels.language) || 
                                (voice.languages && voice.languages.length > 0 ? voice.languages.map((lang: string) => 
                                  lang === "tr" ? "Türkçe" : 
                                  lang === "en" ? "İngilizce" : 
                                  lang.toUpperCase()
                                ).join(", ") : "Belirsiz")}
                            </TableCell>
                            <TableCell>
                              <Badge variant="outline">{voice.gender === 'female' ? 'Kadın' : voice.gender === 'male' ? 'Erkek' : voice.gender === 'other' ? 'Diğer' : 'Belirsiz'}</Badge>
                            </TableCell>
                            <TableCell>
                              <div className="flex flex-wrap gap-1">
                                {voice.tags && voice.tags.map((tag, index) => (
                                  <Badge key={index} variant="secondary">
                                    {tag}
                                  </Badge>
                                ))}
                                {(!voice.tags || voice.tags.length === 0) && (
                                  <span className="text-xs text-muted-foreground">
                                    Etiket yok
                                  </span>
                                )}
                              </div>
                            </TableCell>
                            <TableCell>
                              {/* Audio elementleri kaldırıldı - Artık JavaScript Audio API kullanılıyor */}
                              <Button
                                variant="ghost"
                                size="sm"
                                data-playing="false"
                                data-voice-id={voice.voice_id}
                                onClick={(e) => toggleVoicePreview(voice.voice_id, voice.previewAudioUrl, e.currentTarget as HTMLButtonElement)}
                              >
                                <Play className="h-4 w-4 mr-1" />
                                Dinle
                              </Button>
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center space-x-2">
                                <Switch
                                  checked={voice.isActive}
                                  onCheckedChange={(checked) => toggleVoiceEnabled(voice.voiceId || voice.voice_id, checked)}
                                />
                                <span className={voice.isActive ? "text-green-600" : "text-gray-500"}>
                                  {voice.isActive ? "Aktif" : "Devre Dışı"}
                                </span>
                              </div>
                            </TableCell>
                            <TableCell className="text-right">
                              <div className="flex items-center justify-end gap-1">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleEditVoice(voice)}
                                >
                                  <Edit className="h-4 w-4 mr-1" />
                                  Düzenle
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="text-destructive hover:text-destructive hover:bg-destructive/10"
                                  onClick={() => setDeletingVoice(voice)}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))
                      ) : (
                        <TableRow>
                          <TableCell colSpan={7} className="text-center py-6">
                            {searchQuery || selectedLanguage !== "all" || selectedGender !== "all" ? (
                              <div className="flex flex-col items-center">
                                <Search className="h-8 w-8 mb-2 text-muted-foreground" />
                                <p>Arama kriterlerine uygun ses modeli bulunamadı</p>
                                <Button
                                  variant="link"
                                  onClick={() => {
                                    setSearchQuery("");
                                    setSelectedLanguage("all");
                                    setSelectedGender("all");
                                  }}
                                >
                                  Filtreleri Temizle
                                </Button>
                              </div>
                            ) : (
                              <div className="flex flex-col items-center">
                                <VolumeX className="h-8 w-8 mb-2 text-muted-foreground" />
                                <p>Herhangi bir ses modeli bulunamadı</p>
                                <p className="text-sm text-muted-foreground">
                                  ElevenLabs API ayarlarını kontrol edin
                                </p>
                              </div>
                            )}
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="language-settings">
          <Card>
            <CardHeader>
              <CardTitle>Dil Ayarları</CardTitle>
              <CardDescription>
                AI anons sisteminde kullanılabilir dilleri ve varsayılan dil ayarlarını yapılandırın
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div>
                <Label htmlFor="defaultLanguage">Varsayılan Dil</Label>
                <Select 
                  value={settings?.defaultLanguage || "tr"} 
                  onValueChange={(value) => updateVoiceSettings.mutate({ defaultLanguage: value })}
                >
                  <SelectTrigger id="defaultLanguage">
                    <SelectValue placeholder="Varsayılan dil seçin" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="tr">Türkçe</SelectItem>
                    <SelectItem value="en">İngilizce</SelectItem>
                    {settings?.availableLanguages?.filter((lang: string) => lang !== "tr" && lang !== "en").map((lang: string) => (
                      <SelectItem key={lang} value={lang}>
                        {lang.toUpperCase()}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-sm text-muted-foreground mt-1">
                  Kullanıcı arayüzünde varsayılan olarak seçili gelecek dil
                </p>
              </div>

              <div>
                <Label>Kullanılabilir Diller</Label>
                <div className="border rounded-md p-4 mt-1.5 space-y-3">
                  <div className="flex items-center space-x-2">
                    <Checkbox 
                      id="tr-lang" 
                      checked={settings?.availableLanguages?.includes("tr")} 
                      onChange={(e) => {
                        const checked = e.target.checked;
                        // Dil listesini güncelle
                        const currentLangs = [...(settings?.availableLanguages || [])];
                        let newLangs;
                        
                        if (checked) {
                          newLangs = [...currentLangs, "tr"];
                        } else {
                          newLangs = currentLangs.filter(lang => lang !== "tr");
                        }
                        
                        updateVoiceSettings.mutate({ availableLanguages: newLangs });
                      }}
                    />
                    <Label htmlFor="tr-lang">Türkçe</Label>
                  </div>

                  <div className="flex items-center space-x-2">
                    <Checkbox 
                      id="en-lang" 
                      checked={settings?.availableLanguages?.includes("en")} 
                      onChange={(e) => {
                        const checked = e.target.checked;
                        // Dil listesini güncelle
                        const currentLangs = [...(settings?.availableLanguages || [])];
                        let newLangs;
                        
                        if (checked) {
                          newLangs = [...currentLangs, "en"];
                        } else {
                          newLangs = currentLangs.filter(lang => lang !== "en");
                        }
                        
                        updateVoiceSettings.mutate({ availableLanguages: newLangs });
                      }}
                    />
                    <Label htmlFor="en-lang">İngilizce</Label>
                  </div>
                </div>
                <p className="text-sm text-muted-foreground mt-1">
                  Kullanıcıların seçebileceği dil seçenekleri
                </p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Ses Modeli Düzenleme Modalı */}
      <Dialog open={!!editingVoice} onOpenChange={(open) => !open && setEditingVoice(null)}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Ses Modeli Düzenle</DialogTitle>
            <DialogDescription>
              {editingVoice?.name} ses modeli için detayları düzenleyin
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-4 py-4">
            <div>
              <Label htmlFor="voiceName">Ses Modeli Adı</Label>
              <Input
                id="voiceName"
                value={editingVoice?.name || ''}
                onChange={(e) => {
                  if (editingVoice) {
                    setEditingVoice({ ...editingVoice, name: e.target.value });
                  }
                }}
                placeholder="Ses modeli adını girin"
                className="mt-1.5"
              />
              <p className="text-sm text-muted-foreground mt-1">
                Bu isim ElevenLabs ve kullanıcı panelinde de güncellenecek
              </p>
            </div>

            <div>
              <Label>Cinsiyet</Label>
              <Select
                value={editingVoice?.gender || 'other'}
                onValueChange={(value) => {
                  if (editingVoice) {
                    setEditingVoice({ ...editingVoice, gender: value });
                  }
                }}
              >
                <SelectTrigger className="mt-1.5">
                  <SelectValue placeholder="Cinsiyet seçin" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="female">Kadın</SelectItem>
                  <SelectItem value="male">Erkek</SelectItem>
                  <SelectItem value="other">Diğer</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-sm text-muted-foreground mt-1">
                Voice Design modelleri için cinsiyeti manuel olarak ayarlayın
              </p>
            </div>

            <div>
              <Label>Etiketler</Label>
              <div className="flex flex-wrap gap-2 mb-2 mt-1.5">
                {voiceTags.map((tag, index) => (
                  <Badge key={index} variant="secondary" className="flex items-center gap-1">
                    {tag}
                    <button 
                      onClick={() => handleRemoveTag(tag)}
                      className="hover:bg-slate-200 rounded-full p-0.5"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </Badge>
                ))}
                {voiceTags.length === 0 && (
                  <span className="text-muted-foreground text-sm">Henüz etiket eklenmemiş</span>
                )}
              </div>
              <div className="flex space-x-2">
                <Input
                  placeholder="Yeni etiket..."
                  value={newTagInput}
                  onChange={(e) => setNewTagInput(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleAddTag()}
                />
                <Button variant="outline" onClick={handleAddTag}>Ekle</Button>
              </div>
              <p className="text-sm text-muted-foreground mt-1">
                Ses modellerini kategorize etmek için etiketler ekleyin
              </p>
            </div>

            {/* Dil Seçimi */}
            <div className="space-y-2">
              <Label>Desteklenen Diller</Label>
              <div className="flex flex-wrap gap-2 mb-2">
                {voiceLanguages.map((lang) => {
                  const langInfo = AVAILABLE_LANGUAGES.find(l => l.code === lang);
                  return (
                    <Badge key={lang} variant="secondary" className="flex items-center gap-1">
                      {langInfo?.name || lang}
                      <X
                        className="h-3 w-3 cursor-pointer hover:text-destructive"
                        onClick={() => setVoiceLanguages(voiceLanguages.filter(l => l !== lang))}
                      />
                    </Badge>
                  );
                })}
              </div>
              <Select
                value=""
                onValueChange={(value) => {
                  if (value && !voiceLanguages.includes(value)) {
                    setVoiceLanguages([...voiceLanguages, value]);
                  }
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Dil ekle..." />
                </SelectTrigger>
                <SelectContent>
                  {AVAILABLE_LANGUAGES.filter(lang => !voiceLanguages.includes(lang.code)).map((lang) => (
                    <SelectItem key={lang.code} value={lang.code}>
                      {lang.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-sm text-muted-foreground mt-1">
                Bu ses modelinin hangi dillerde kullanılabileceğini seçin
              </p>
            </div>

            <div className="flex items-center space-x-2">
              <Switch
                id="voiceEnabled"
                checked={editingVoice?.isActive}
                onCheckedChange={(checked) => {
                  if (editingVoice) {
                    setEditingVoice({ ...editingVoice, isActive: checked });
                  }
                }}
              />
              <Label htmlFor="voiceEnabled">Ses Modelini Etkinleştir</Label>
            </div>

            {/* Ses ayarları bölümü - ElevenLabs v3 Alpha için sadece Stability */}
            <div className="space-y-4 border rounded-lg p-4">
              <h4 className="font-medium">Ses Ayarları (ElevenLabs v3 Alpha)</h4>

              <div className="space-y-5">
                {/* Stability (Kararlılık) - ElevenLabs v3 Alpha için 3 mod */}
                <div className="space-y-2">
                  <Label>Ses Stili (Stability)</Label>
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      variant={editingVoice?.settings?.stability === 0 ? "default" : "outline"}
                      className="flex-1"
                      onClick={() => {
                        if (editingVoice) {
                          setEditingVoice({
                            ...editingVoice,
                            settings: {
                              ...editingVoice.settings || {},
                              stability: 0
                            }
                          });
                        }
                      }}
                    >
                      Yaratıcı
                    </Button>
                    <Button
                      type="button"
                      variant={editingVoice?.settings?.stability === 0.5 ? "default" : "outline"}
                      className="flex-1"
                      onClick={() => {
                        if (editingVoice) {
                          setEditingVoice({
                            ...editingVoice,
                            settings: {
                              ...editingVoice.settings || {},
                              stability: 0.5
                            }
                          });
                        }
                      }}
                    >
                      Doğal
                    </Button>
                    <Button
                      type="button"
                      variant={editingVoice?.settings?.stability === 1 ? "default" : "outline"}
                      className="flex-1"
                      onClick={() => {
                        if (editingVoice) {
                          setEditingVoice({
                            ...editingVoice,
                            settings: {
                              ...editingVoice.settings || {},
                              stability: 1
                            }
                          });
                        }
                      }}
                    >
                      Tutarlı
                    </Button>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Yaratıcı: Duygusal, ifadeli | Doğal: Dengeli | Tutarlı: Profesyonel, sabit
                  </p>
                </div>
              </div>
            </div>

            <div>
              <Label>Ön İzleme</Label>
              <div className="flex items-center justify-center bg-slate-50 rounded-md p-4 mt-1.5">
                {editingVoice?.preview_url ? (
                  <audio
                    controls
                    src={editingVoice.preview_url}
                    className="w-full"
                  >
                    Tarayıcınız audio elementini desteklemiyor.
                  </audio>
                ) : (
                  <span className="text-muted-foreground">Ön izleme kullanılamıyor</span>
                )}
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingVoice(null)}>
              İptal
            </Button>
            <Button
              onClick={handleSaveVoiceEdit}
              disabled={updateVoiceModel.isPending}
            >
              {updateVoiceModel.isPending ? (
                <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Save className="mr-2 h-4 w-4" />
              )}
              Kaydet
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Ses Modeli Silme Onay Dialogu */}
      <AlertDialog open={!!deletingVoice} onOpenChange={(open) => !open && setDeletingVoice(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Ses Modelini Sil</AlertDialogTitle>
            <AlertDialogDescription>
              <strong>{deletingVoice?.name}</strong> ses modeli hem ElevenLabs hesabınızdan hem de sistemden kalıcı olarak silinecek. Bu işlem geri alınamaz.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteVoiceModel.isPending}>İptal</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={deleteVoiceModel.isPending}
              onClick={() => {
                const voiceId = deletingVoice?.voiceId || deletingVoice?.voice_id;
                if (voiceId) {
                  deleteVoiceModel.mutate(voiceId);
                }
              }}
            >
              {deleteVoiceModel.isPending ? (
                <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Trash2 className="mr-2 h-4 w-4" />
              )}
              Kalıcı Olarak Sil
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default VoiceModelSettings;
